import React, { useState, useEffect, useRef  } from "react";
import { useNavigate } from "react-router-dom";
import IconButton from "components/common/iconButton";
import { Link } from "react-router-dom";
import { Card, Col, Row, Modal, Spinner, Button } from "react-bootstrap";
import paths from "routes/paths";
import axios from "axios";
import SubtleBadge from "components/common/subtleBadge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AdvanceTable from "components/common/advance-table/advanceTable";
import AdvanceTableSearchBox from "components/common/advance-table/advanceTableSearchBox";
import AdvanceTablePagination from "components/common/advance-table/advanceTablePagination";
import AdvanceTableFooter from 'components/common/advance-table/advanceTableFooter';
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/advanceTableProvider";
import corner4 from "assets/img/illustrations/corner-4.png";
import FalconCloseButton from "components/common/falconCloseButton";
import Background from "components/common/background";
import { toast } from "react-toastify";
import { formatDate } from "assets/js/common"; import { fetchRoleMappings } from "../../assets/js/common";
import { useTranslation } from "react-i18next";
import { fetchRoleAndClaims, handleLoadingAndAccess, getInitialPageSize } from "../../assets/js/common";
import { useAppContext } from '../../providers/appProvider';
import { getDomainConfig } from "../../config/domainConfig";
import BulkImportDrawer from '../common/bulkImportDrawer';
import { usersImportConfig } from '../../config/importConfigs/usersImportConfig';

const Users = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]); // State for user data
  const [loading, setLoading] = useState(true); // State for loading
  const [selecteduserId, setSelecteduserId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [allLocations, setAllLocations] = useState(null);
  const { config } = useAppContext();
  const { t, i18n } = useTranslation();
  const handleClose = () => setShowModal(false);
  const [customerId, setCustomerId] = useState(null);
  const [error, setError] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const usersConfig = usersImportConfig();
  const handleShow = (userId) => {
    setSelecteduserId(userId);
    setShowModal(true);
  };
  const domainConfig = getDomainConfig();
  const currencySymbol = domainConfig.financialLabels.currencySymbol;

  useEffect(() => {
    fetchRoleAndClaims({
      navigate,
      claimType: "users",
      claimValue: "view",
      setHasAccess,
      setIsLoading,
    });

    fetchLocations();
  }, [navigate]);

  const fetchLocations = async () => {
    setLoadingLocations(true);
    try {
      const customerId = localStorage.getItem("customerId");
      setCustomerId(customerId);

      if (!customerId) {
        setError("CustomerId is missing.");
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/locationRoute/all`,
        {
          params: { customerId },
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        },
      );
      setAllLocations(response.data);
    } catch (err) {
      setError("Failed to fetch branches. Please try again.");
    } finally {
      setLoadingLocations(false);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const selectedCustomerId = localStorage.getItem("customerId");
        const userLocationId = localStorage.getItem("LocationId");

        if (!selectedCustomerId) {
          setLoadingUsers(false);
          console.error("CustomerId is missing");
          return;
        }

        const usersResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/userRoute/users/allLocations`,
          {
            params: { customerId: selectedCustomerId },
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret
            }
          }
        );

        const rolePriority = {
          [t("adminText")]: 1,
          [t("appOwnerText")]: 2,
          [t("employeeText")]: 3,
        };
        const sortedData = await Promise.all(
          usersResponse.data.map(async (user) => {
            const roleName = (await fetchRoleMappings(user.Role, t)) || "Onbekend";
            return {
              ...user,
              RoleName: roleName,
            };
          })
        ).then((users) =>
          users.sort((a, b) => {
            const roleA = rolePriority[a.RoleName] || 4;
            const roleB = rolePriority[b.RoleName] || 4;

            if (roleA !== roleB) {
              return roleA - roleB;
            }

            const dateA = new Date(a.updatedAt || a.createdAt);
            const dateB = new Date(b.updatedAt || b.createdAt);
            return dateB - dateA;
          })
        );

        const activeUsers = sortedData.filter((u) => !u.IsDelete);

        const visibleData = activeUsers.filter((user) => {
          const role = user.RoleName;

          // App Owner & Admin → always visible
          if (
            role === t("appOwnerText") ||
            role === t("adminText")
          ) {
            return true;
          }

          // Employee → match selected location
          if (role === t("employeeText")) {
            return user.locations?.some(
              l => String(l.LocationId) === String(userLocationId)
            );
          }

          return false;
        });

        //  Apply globalFilter
        const filteredData = visibleData.filter((row) => {
          const {
            UserNumber,
            Name,
            PhoneNumber,
            Emailaddress,
            RoleName,
            DateOfJoining,
            Features,
            Status,
          } = row;

          const dateString = DateOfJoining ? formatDate(DateOfJoining) : "";
          const statusString = Status === "0" ? "Actief" : "Inactief";

          return (
            (UserNumber &&
              UserNumber.toLowerCase().includes(globalFilter.toLowerCase())) ||
            (Name && Name.toLowerCase().includes(globalFilter.toLowerCase())) ||
            (Emailaddress &&
              Emailaddress.toLowerCase().includes(globalFilter.toLowerCase())) ||
            (RoleName &&
              RoleName.toLowerCase().includes(globalFilter.toLowerCase())) ||
            (PhoneNumber &&
              PhoneNumber.toLowerCase().includes(globalFilter.toLowerCase())) ||
            (dateString &&
              dateString.toLowerCase().includes(globalFilter.toLowerCase())) ||
            (statusString &&
              statusString.toLowerCase().includes(globalFilter.toLowerCase())) ||
            (Features &&
              Features.toLowerCase().includes(globalFilter.toLowerCase()))
          );
        });

        setData(filteredData);
        setLoadingUsers(false);
      } catch (error) {
        console.error("Error fetching users or roles data:", error);
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [globalFilter, config.location, i18n.language, refreshKey]);

  const columns = [
    {
      accessorKey: "UserNumber",
      header: t("number"),
      cell: ({ row: { original } }) => <span>{original.UserNumber}</span>,
    },
    {
      accessorKey: "Name",
      header: t("name"),
      cell: ({ row: { original } }) => <span>{original.Name}</span>,
    },
    {
      accessorKey: "PhoneNumber",
      header: t("phoneNumber"),
      cell: ({ row: { original } }) => <span>{original.PhoneNumber}</span>,
    },
    {
      accessorKey: "Emailaddress",
      header: t("email"),
      cell: ({ row: { original } }) => <span>{original.Emailaddress}</span>,
    },
    {
      accessorKey: "RoleName",
      header: t("roleText"),
      enableSorting: true,
      cell: ({ row: { original } }) => <span>{original.RoleName}</span>,
    },
    {
      accessorKey: "manager",
      header: t("manager"),
      cell: ({ row: { original } }) => <span>{original.manager?.Name || "--"}</span>,
    },
    {
      accessorKey: "locations",
      header: t("branch"),
      cell: ({ row: { original } }) => {
        if (!original.locations?.length) {
          return <span>--</span>;
        }
        const selectedLocationId = localStorage.getItem("LocationId");
        // Resolve location names with selected one first
        const resolvedLocations = original.locations
          .map(ul => {
            const location = allLocations.find(
              l => String(l.LocationId) === String(ul.LocationId)
            );
            return location
              ? {
                LocationId: ul.LocationId,
                Name: location.Name
              }
              : null;
          })
          .filter(Boolean)
          .sort((a, b) => {
            if (String(a.LocationId) === String(selectedLocationId)) return -1;
            if (String(b.LocationId) === String(selectedLocationId)) return 1;
            return 0;
          });

        const visibleNames = resolvedLocations.slice(0, 2).map(l => l.Name);
        const hasMore = resolvedLocations.length > 2;

        return (
          <span>
            {visibleNames.join(", ")}
            {hasMore && " ..."}
          </span>
        );
      },
    },
    {
      accessorKey: "Status",
      header: t("status"),
      cell: ({ row: { original } }) => (
        <SubtleBadge
          pill
          bg={original.Status === "0" ? "success" : "warning"}
          className="d-flex flex-center"
        >
          <p className="mb-0 data-font-size">
            {original.Status === "0" ? `${t("active")}` : `${t("deactive")}`}
          </p>
          <FontAwesomeIcon
            icon={original.Status === "0" ? "check" : "ban"}
            transform="shrink-2"
            className="ms-1"
          />
        </SubtleBadge>
      ),
    },
    {
      accessorKey: "FixedHours",
      header: t("fixedHours"),
      cell: ({ row: { original } }) => {
        const hours = original.FixedHours;
        const formattedHours =
          typeof hours === "number" ? String(hours).replace(".", ",") : "--";
        return <span>{formattedHours}</span>;
      },
    },
    {
      accessorKey: "HourlyRate",
      header: t("hourlyRate"),
      cell: ({ row: { original } }) => {
        const rate = original.HourlyRate;
        const formattedRate =
          typeof rate === "number" ? String(rate).replace(".", ",") : "--";
        return <span>{currencySymbol} {formattedRate}</span>;
      },
    },
    {
      accessorKey: "Weekdays",
      header: t("weekdays"),
      cell: ({ row: { original } }) => {
        if (!original.Weekdays) return <span>--</span>;

        let weekdays = [];
        try {
          weekdays = JSON.parse(original.Weekdays); // ["monday","tuesday"]
        } catch {
          return <span>--</span>;
        }

        if (!Array.isArray(weekdays) || !weekdays.length) {
          return <span>--</span>;
        }

        const translatedDays = weekdays.map(day =>
          t(`days.${day.toLowerCase()}`)
        );

        const visibleDays = translatedDays.slice(0, 3);
        const hasMore = translatedDays.length > 3;

        return (
          <div
            className="weekdays-badges-ellipsis"
            title={translatedDays.join(", ")} // full list tooltip
          >
            {visibleDays.map((day) => (
              <SubtleBadge
                key={day}
                pill
                bg="success"
                className="me-1 text-capitalize d-inline-flex flex-center data-font-size"
              >
                <p className="mb-0">{day}</p>
              </SubtleBadge>
            ))}

            {hasMore && (
              <span className="weekdays-dots">...</span>
            )}
          </div>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data,
    columns,
    selection: false,
    sortable: true,
    pagination: true,
    perPage: getInitialPageSize('usersTable')
  });

  const loadingOrRedirect = handleLoadingAndAccess(
    isLoading,
    hasAccess
  );

  if (loadingOrRedirect) {
    return loadingOrRedirect; // Render the loading spinner or handle navigation
  }

  const handleViewDetails = (userId) => {
    navigate(`${paths.userDetails.replace(":userId", userId.toLowerCase())}`);
  };

  const deleteUser = async (userId) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/userRoute/deleteUser/${userId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      if (response.status === 200) {
        toast.success("Gebruikers is verwijderd", {
          position: "top-right",
          autoClose: 5000,
        });
        setTimeout(() => {
          window.location.reload();
        }, 300);
      }
    } catch (error) {
      toast.error(
        "Er is iets misgegaan, vul alstublieft het formulier opnieuw in",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      console.error(
        "Error deleting relation:",
        error.response ? error.response.data : error.message
      );
    }
  };

  // Modal component for deletion confirmation
  const StaticBackdropModal = ({ show, handleClose, userId }) => {
    return (
      <Modal show={show} onHide={handleClose} backdrop="static">
        <Modal.Header>
          <Modal.Title>Bevestiging</Modal.Title>
          <FalconCloseButton onClick={handleClose} />
        </Modal.Header>
        <Modal.Body>
          Weet u zeker dat u deze gebruiker en de bijbehorende gegevens wilt
          verwijderen?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Annuleren
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              deleteUser(userId);
              handleClose();
            }}
          >
            Verwijderen
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // const fetchLocation = (loactionId) => {
  //   const location = allLocations.find(
  //     location => location.LocationId === loactionId
  //   )
  //   return location.Name;
  // }

  return (
    <>
      <AdvanceTableProvider {...table} >
        <Row className="g-3 mb-3">
          <Col lg={12} xxl={12}>
            <Card className="mb-3 h-100">
              <Background image={corner4} className="bg-card" />
              <Card.Body className="position-relative pe-3 pb-2">
                <Row className="flex-between-center">
                  <Col md>
                    <h5 className="mb-2 mb-md-0">{t("users")}</h5>
                  </Col>
                  <Col xs="auto" className="d-flex gap-2">
                    <BulkImportDrawer
                      config={usersConfig}
                      onImportComplete={() => setRefreshKey((k) => k + 1)}
                    />
                    <IconButton
                      variant="primary"
                      as={Link}
                      to={paths.newUser}
                      size="sm"
                      icon="plus"
                      transform="shrink-3"
                      data-tour="new-user-btn"
                    >
                      <span className="ms-1"> {t("newUser")}</span>
                    </IconButton>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Card className="mb-3">
          <Card.Header>
            <Row className="flex-between-center">
              <Col xs={8} sm="auto" className="ms-auto text-end ps-0">
                <AdvanceTableSearchBox setGlobalFilter={setGlobalFilter} />
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            {(loadingLocations || loadingUsers || !customerId || !allLocations) ? (
             <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">{t("loading")}</span>
              </Spinner>
            </div>
            ) : (
              <>
                <AdvanceTable
                  headerClassName="bg-200 fs-9 text-nowrap align-middle text-center"
                  rowClassName="align-middle white-space-nowrap"
                  tableProps={{
                    bordered: true,
                    striped: true,
                    size: "sm",
                    className: "mb-0 overflow-hidden data-font-size table-hover",
                  }}
                  getRowProps={(row) => ({
                    onClick: () => handleViewDetails(row.original.UserId),
                    style: { cursor: "pointer" }
                  })}
                />
                {data.length === 0 ? (
                  <div className="text-center pt-4">
                    {t("noDataAvailableText")}
                  </div>
                ) : null}
              </>
            )}
          </Card.Body>
          <Card.Footer>
            {/* <AdvanceTablePagination /> */}
            <AdvanceTableFooter
              rowInfo
              navButtons
              rowsPerPageSelection
              tableKey="usersTable"
            />
          </Card.Footer>
        </Card>
      </AdvanceTableProvider>
      <StaticBackdropModal
        show={showModal}
        handleClose={handleClose}
        userId={selecteduserId}
      />
    </>
  );
};

export default Users;